import React, { useState, useEffect } from "react";
import { Header } from "../Header";
import { useLocation } from "react-router-dom";
import CircularProgress from '@mui/material/CircularProgress';
import './Customer.css';
import { local_url, local_signage } from "../Const";
import { FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const API_URL = `${local_url}/api/Ledger/GetCreditBalance/L111/ALL`;

export const CustomerLedger = () => {
  const location = useLocation();
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filters state
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState("");
  const [areas, setAreas] = useState([]);
  const [area, setArea] = useState("ALL");
  const [branchList, setBranchList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchInputValue, setBranchInputValue] = useState("");
  
  // Date filters
  const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'));

  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('AuthorizationToken');

  const handleAuthError = () => {
    console.error("Session expired or unauthorized access.");
    localStorage.clear();
    sessionStorage.clear();
    alert("Session expired. Please log in again.");
    window.location.href = "/login";
  };

  const handleBranchTypeChange = (event) => {
    const value = event.target.value;
    setBranchType(value);
    setSelectedBranch(null); // Reset branch selection when segment changes
    setBranchInputValue("");
  };

  const handleAreaChange = (event) => {
    const value = event.target.value;
    setArea(value);
    setSelectedBranch(null); // Reset branch selection when area changes
    setBranchInputValue("");
    
    // Fetch branch list for the selected area
    if (branchType) {
      fetchBranchList("AREA", branchType);
    }
  };

  const handleBranchChange = (event, newValue) => {
    setSelectedBranch(newValue);
  };

  const handleFromDateChange = (newValue) => {
    setFromDate(newValue.format('YYYY-MM-DD'));
  };

  const handleToDateChange = (newValue) => {
    setToDate(newValue.format('YYYY-MM-DD'));
  };

  // Fetch business segments when component mounts
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await fetch(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch business segments');
        }

        const data = await response.json();
        console.log('Fetched business segments:', data);

        // Filter out deleted segments
        const activeSegments = data.filter(segment => segment.deleted === "N");
        setSegments(activeSegments);
      } catch (error) {
        console.error('Error fetching business segments:', error);
      }
    };

    if (token) {
      fetchSegments();
    }
  }, [token]);

  // Fetch areas when segment changes
  useEffect(() => {
    const fetchAreas = async () => {
      if (!branchType) return;

      try {
        const response = await fetch(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch areas by segment');
        }

        const data = await response.json();
        console.log('Fetched areas for segment:', data);
        setAreas(data);
        // Reset area selection to ALL
        setArea("ALL");
      } catch (error) {
        console.error('Error fetching areas by segment:', error);
        setAreas([]);
      }
    };

    if (token && branchType) {
      fetchAreas();
    }
  }, [branchType, token]);

  // Fetch branch list when area changes
  const fetchBranchList = async (fetchType, value) => {
    if (!token) {
      console.error('Authentication token is missing');
      handleAuthError();
      return;
    }

    try {
      // Use the selected area ID for API call
      const areaParam = area || "ALL";
      const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${value}`;

      console.log('Fetching branches from URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Failed to fetch branch list');
      }

      const data = await response.json();
      console.log('Fetched branch list:', data);
      setBranchList(data);
    } catch (error) {
      console.error('Error fetching branch list:', error);
      setBranchList([]);
    }
  };

  useEffect(() => {
    if (branchType && area) {
      fetchBranchList("AREA", branchType);
    }
  }, [area, branchType]);

  const fetchLedgerData = async () => {
    if (!selectedBranch) {
      setError("Please select a branch to view ledger data");
      return;
    }
    
    console.log("Fetching ledger data...");
    setLoading(true);
    setError(null);

    try {
      // Retrieve token from localStorage
      const token = localStorage.getItem('AuthorizationToken');
      if (!token) {
        console.error("Authorization token is missing.");
        setError("Authorization token is missing. Please log in again.");
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
        setLoading(false);
        return;
      }

      // Prepare API URL with filters
      let apiUrl = `${local_url}/api/Ledger/GetCreditBalance/${selectedBranch.branchID}/ALL`;
      
      // API call with Authorization header
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Check response status
      if (response.status === 401) {
        console.error("Session expired or unauthorized access.");
        localStorage.clear();
        sessionStorage.clear();
        alert("Session expired. Please log in again.");
        window.location.href = "/login";
        return;
      }

      if (!response.ok) {
        throw new Error(`Network response was not ok. Status: ${response.status}`);
      }

      // Parse and update data
      const data = await response.json();
      setLedgerData(data);
    } catch (error) {
      console.error("Error fetching ledger data:", error);
      setError("Failed to fetch ledger data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial data fetch is skipped, now requires filter selection first
  }, []);

  const getCreditStatusClass = (creditLimit, availablecredit) => {
    if (!creditLimit) return '';
    
    const ratio = availablecredit / creditLimit;
    if (ratio > 0.7) return 'status-good';
    if (ratio > 0.3) return 'status-warning';
    return 'status-critical';
  };

  const exportToCSV = () => {
    const dataToExport = ledgerData.map(item => ({
      CustomerID: item.custId,
      CustomerName: item.customerName,
      Mobile: item.mobile || 'N/A',
      CreditLimit: item.creditLimit,
      CreditDays: item.creditDays,
      DueSince: new Date(item.dueSince).toLocaleDateString(),
      CreditAmount: item.creditamount,
      AvailableCredit: item.availablecredit,
    }));

    const csvRows = [];
    const headers = Object.keys(dataToExport[0]);
    csvRows.push(headers.join(','));

    for (const row of dataToExport) {
      csvRows.push(headers.map(header => 
        JSON.stringify(row[header], (key, value) => value === null ? '' : value)
      ).join(','));
    }

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'customer_ledger.csv');
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Header />
      <div className="ledger-layout" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="fixed-header" style={{ flexShrink: 0, backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', zIndex: 10, padding: '20px' }}>
          {error && (
            <div className="mb-4 p-4 bg-red-100 text-red-600 rounded">
              {error}
            </div>
          )}
    
          <div className="header-container">
            <div className="filters-container">
            {/* Branch Type (Segment) Dropdown */}
            <FormControl variant="outlined" style={{ minWidth: 150 }}>
              <InputLabel id="branch-type-select-label">Segment</InputLabel>
              <Select
                labelId="branch-type-select-label"
                value={branchType}
                onChange={handleBranchTypeChange}
                label="Segment"
              >
                {segments.map((segment) => (
                  <MenuItem key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                    {segment.businessSegmentName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Area Dropdown */}
            <FormControl variant="outlined" style={{ minWidth: 130, maxWidth: 130 }}>
              <InputLabel id="area-select-label">Area</InputLabel>
              <Select
                labelId="area-select-label"
                value={area}
                onChange={handleAreaChange}
                label="Area"
                disabled='true' 
              >
                <MenuItem value="ALL">All</MenuItem>
                {areas.map((area) => (
                  <MenuItem key={area.SFAreaId} value={area.SFAreaId}>
                    {area.SFAreaName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Branch Autocomplete */}
            {branchType && (
              <Autocomplete
                style={{ minWidth: 180, maxWidth: 180 }}
                options={branchList || []}
                getOptionLabel={(option) => option.branchName || ""}
                value={selectedBranch}
                onChange={handleBranchChange}
                inputValue={branchInputValue}
                onInputChange={(event, newInputValue) => {
                  setBranchInputValue(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Branch"
                    variant="outlined"
                    size="medium"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.branchID === value?.branchID}
              />
            )}

            {/* Date Pickers */}
            {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={dayjs(fromDate)}
                onChange={handleFromDateChange}
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 150,
                      backgroundColor: '#F1F8E9',
                      '& .MuiOutlinedInput-root': {
                        height: 40,
                      }
                    },
                  },
                }}
              />
            </LocalizationProvider>
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={dayjs(toDate)}
                onChange={handleToDateChange}
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 150,
                      backgroundColor: '#F1F8E9',
                      '& .MuiOutlinedInput-root': {
                        height: 40,
                      }
                    },
                  },
                }}
              />
            </LocalizationProvider> */}
            
            {/* Action Buttons */}
            <button 
              onClick={fetchLedgerData}
              className="filter-button"
            >
              Apply Filter
            </button>
            
            <button 
              onClick={exportToCSV} 
              disabled={loading || ledgerData.length === 0}
              className="filter-button"
              style={{ backgroundColor: '#4CAF50' }}
            >
              Export to CSV
            </button>
          </div>
        </div>
        </div>
        
        <div className="scrollable-content" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress />
            </div>
          ) : (
            <div className="table-container">
              <table className="Ledger-balance">
                <thead>
                  <tr>
                    <th>Customer ID</th>
                    <th>Customer Name</th>
                    <th>Mobile</th>
                    <th>Credit Limit</th>
                    <th>Credit Days</th>
                    <th>Due Since</th>
                    <th>Credit Amount</th>
                    <th>Available Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerData.length > 0 ? (
                    ledgerData.map((item) => (
                      <tr key={item.custId}>
                        <td>{item.custId}</td>
                        <td>{item.customerName}</td>
                        <td>{item.mobile || 'N/A'}</td>
                        <td>₹{item.creditLimit.toLocaleString('en-IN')}</td>
                        <td>{item.creditDays}</td>
                        <td>{new Date(item.dueSince).toLocaleDateString('en-IN')}</td>
                        <td>₹{item.creditamount.toLocaleString('en-IN')}</td>
                        <td className={getCreditStatusClass(item.creditLimit, item.availablecredit)}>
                          ₹{item.availablecredit.toLocaleString('en-IN')}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="no-data">
                        No ledger data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CustomerLedger;